import React, { useState, useEffect, useCallback } from 'react';
import { HardDrive, Image, Trash2, RefreshCw, AlertTriangle, ChevronDown, Check, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const StatCard = ({ label, value, subtext, icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-2xl font-bold text-[#1a3a5c]">{value}</p>
    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
  </div>
);

const CreditsBar = ({ used, limit, percent }) => {
  const barColor = percent > 80 ? '#ef4444' : percent > 50 ? '#f59e0b' : '#22c55e';
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="stat-credits">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Credits Used</p>
        <span className="text-xs font-semibold text-[#1a3a5c]">{used} / {limit}</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-3 mt-2">
        <div
          className="h-3 rounded-full transition-all duration-500"
          style={{ width: `${Math.min(percent, 100)}%`, backgroundColor: barColor }}
        />
      </div>
      <p className="text-xs text-gray-400 mt-2">{percent}% of monthly credits used</p>
    </div>
  );
};

export default function StorageTab() {
  const [usage, setUsage] = useState(null);
  const [resources, setResources] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [deletedBytes, setDeletedBytes] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await api.get('/cloudinary/usage');
      setUsage(res.data);
    } catch (err) {
      if (err.response?.status === 503) {
        setNotConfigured(true);
      } else {
        toast.error('Failed to load storage usage');
      }
    }
  }, []);

  const fetchResources = useCallback(async (cursor = null, append = false) => {
    try {
      const params = { max_results: 30 };
      if (cursor) params.next_cursor = cursor;
      const res = await api.get('/cloudinary/resources', { params });
      if (append) {
        setResources(prev => [...prev, ...res.data.resources]);
      } else {
        setResources(res.data.resources);
      }
      setNextCursor(res.data.next_cursor || null);
    } catch (err) {
      if (err.response?.status === 503) {
        setNotConfigured(true);
      }
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchUsage(), fetchResources()]);
      setLoading(false);
    };
    init();
  }, [fetchUsage, fetchResources]);

  const handleRefresh = async () => {
    setLoading(true);
    setSelected(new Set());
    setDeletedBytes(0);
    setDeletedCount(0);
    await Promise.all([fetchUsage(), fetchResources()]);
    setLoading(false);
  };

  const handleLoadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    await fetchResources(nextCursor, true);
    setLoadingMore(false);
  };

  const toggleSelect = (publicId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === resources.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(resources.map(r => r.public_id)));
    }
  };

  const handleDelete = async () => {
    if (selected.size === 0) return;
    const confirmed = window.confirm(`Delete ${selected.size} image(s) from Cloudinary? This cannot be undone.`);
    if (!confirmed) return;

    // Calculate bytes being deleted for local estimate
    const bytesDeleted = resources
      .filter(r => selected.has(r.public_id))
      .reduce((sum, r) => sum + (r.bytes || 0), 0);

    setDeleting(true);
    try {
      const res = await api.delete('/cloudinary/bulk-delete', { data: Array.from(selected) });
      const count = res.data.deleted_count || 0;
      toast.success(`Deleted ${count} image(s)`);
      setDeletedBytes(prev => prev + bytesDeleted);
      setDeletedCount(prev => prev + count);
      setResources(prev => prev.filter(r => !selected.has(r.public_id)));
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (publicId) => {
    const confirmed = window.confirm('Delete this image from Cloudinary?');
    if (!confirmed) return;

    const resource = resources.find(r => r.public_id === publicId);
    try {
      await api.delete('/cloudinary/delete', { params: { public_id: publicId } });
      toast.success('Image deleted');
      setDeletedBytes(prev => prev + (resource?.bytes || 0));
      setDeletedCount(prev => prev + 1);
      setResources(prev => prev.filter(r => r.public_id !== publicId));
      selected.delete(publicId);
      setSelected(new Set(selected));
    } catch (err) {
      toast.error('Failed to delete image');
    }
  };

  // Format bytes helper
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < units.length - 1) { size /= 1024; i++; }
    return `${size.toFixed(1)} ${units[i]}`;
  };

  if (notConfigured) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center" data-testid="storage-not-configured">
        <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-amber-800 mb-2">Cloudinary Not Configured</h3>
        <p className="text-amber-700 text-sm">Please add your Cloudinary credentials in the <strong>Settings</strong> tab to use Storage management.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="storage-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#C9A967] border-t-transparent mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading storage data...</p>
        </div>
      </div>
    );
  }

  // Calculate adjusted storage display
  const adjustedStorageBytes = Math.max(0, (usage?.storage?.used_bytes || 0) - deletedBytes);
  const adjustedImageCount = Math.max(0, (usage?.resources || 0) - deletedCount);

  return (
    <div className="space-y-6" data-testid="storage-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold text-[#1a3a5c]">Cloud Storage</h2>
          <p className="text-gray-500 text-sm">
            Manage your Cloudinary images and monitor usage
            {usage?.plan && <span className="ml-2 px-2 py-0.5 bg-[#1a3a5c]/10 text-[#1a3a5c] rounded text-xs font-medium">{usage.plan}</span>}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          data-testid="refresh-storage"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Stats Grid */}
      {usage && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="storage-stats">
            <StatCard
              label="Storage"
              value={deletedBytes > 0 ? formatBytes(adjustedStorageBytes) : usage.storage.used_display}
              subtext={deletedBytes > 0 ? `${formatBytes(deletedBytes)} freed this session` : `${usage.storage.credits_used} credits`}
            />
            <StatCard
              label="Bandwidth"
              value={usage.bandwidth.used_display}
              subtext={`${usage.bandwidth.credits_used} credits this month`}
            />
            <StatCard
              label="Images"
              value={deletedCount > 0 ? adjustedImageCount.toLocaleString() : (usage.resources?.toLocaleString() || '0')}
              subtext={deletedCount > 0 ? `${deletedCount} deleted this session` : 'Total uploaded'}
            />
            <StatCard
              label="Transformations"
              value={usage.transformations.usage?.toLocaleString() || '0'}
              subtext={`${usage.transformations.credits_used} credits`}
            />
          </div>

          {/* Credits Progress Bar */}
          {usage.credits && (
            <CreditsBar
              used={usage.credits.used}
              limit={usage.credits.limit}
              percent={usage.credits.used_percent}
            />
          )}

          {/* Stats delay note */}
          {(deletedCount > 0 || usage.last_updated) && (
            <div className="flex items-start gap-2 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-lg">
              <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                {deletedCount > 0
                  ? `You freed ~${formatBytes(deletedBytes)} this session. Cloudinary dashboard stats update once daily (last updated: ${usage.last_updated}). Click Refresh to see the latest server-side stats.`
                  : `Stats last updated: ${usage.last_updated}. Cloudinary updates usage stats once daily.`
                }
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={toggleSelectAll}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              selected.size === resources.length && resources.length > 0
                ? 'bg-[#1a3a5c] text-white border-[#1a3a5c]'
                : 'text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
            data-testid="select-all-btn"
          >
            <Check className="h-3.5 w-3.5" />
            {selected.size === resources.length && resources.length > 0 ? 'Deselect All' : 'Select All'}
          </button>
          {selected.size > 0 && (
            <span className="text-sm text-gray-500">
              {selected.size} selected
              <span className="text-gray-400 ml-1">
                ({formatBytes(resources.filter(r => selected.has(r.public_id)).reduce((s, r) => s + (r.bytes || 0), 0))})
              </span>
            </span>
          )}
        </div>
        {selected.size > 0 && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-2 px-4 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 disabled:opacity-50 transition-colors"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {deleting ? 'Deleting...' : `Delete ${selected.size}`}
          </button>
        )}
      </div>

      {/* Images Grid */}
      {resources.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center" data-testid="no-resources">
          <Image className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No images found</p>
          <p className="text-gray-400 text-sm mt-1">Upload images via product or homepage editors</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="resources-grid">
          {resources.map((r) => {
            const isSelected = selected.has(r.public_id);
            return (
              <div
                key={r.public_id}
                className={`group relative bg-white rounded-xl border-2 overflow-hidden transition-all cursor-pointer ${
                  isSelected ? 'border-[#1a3a5c] ring-2 ring-[#1a3a5c]/20' : 'border-gray-100 hover:border-gray-300'
                }`}
                onClick={() => toggleSelect(r.public_id)}
                data-testid={`resource-${r.public_id.replace(/\//g, '-')}`}
              >
                {/* Selection indicator */}
                <div className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                  isSelected ? 'bg-[#1a3a5c] border-[#1a3a5c]' : 'border-white/80 bg-black/20 group-hover:bg-white/80 group-hover:border-gray-300'
                }`}>
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </div>

                {/* Image */}
                <div className="aspect-square bg-gray-50">
                  <img
                    src={r.url}
                    alt={r.public_id.split('/').pop()}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                {/* Info */}
                <div className="p-2.5">
                  <p className="text-xs font-medium text-[#1a3a5c] truncate" title={r.public_id}>
                    {r.public_id.split('/').pop()}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-gray-400">
                      {r.size_display} · {r.format?.toUpperCase()}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSingle(r.public_id); }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-all"
                      title="Delete"
                      data-testid={`delete-${r.public_id.replace(/\//g, '-')}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {r.width > 0 && (
                    <span className="text-[10px] text-gray-300">{r.width}x{r.height}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {nextCursor && (
        <div className="text-center pt-2">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            data-testid="load-more-btn"
          >
            <ChevronDown className="h-4 w-4" />
            {loadingMore ? 'Loading...' : 'Load More Images'}
          </button>
        </div>
      )}
    </div>
  );
}
